
import React, { useState } from 'react';
import { X, DollarSign, CreditCard, Landmark, Wallet, Layers, CheckCircle, Printer } from 'lucide-react';
import { formatCurrency } from '../utils/format';
import saleService, { Sale, PaymentMethod } from '../services/saleService';
import toast from 'react-hot-toast';

interface PaymentNoteModalProps {
    sale: Sale;
    onClose: () => void;
    onSuccess: () => void;
}

export const PaymentNoteModal: React.FC<PaymentNoteModalProps> = ({ 
    sale, 
    onClose, 
    onSuccess 
}) => {
    const [amount, setAmount] = useState<number>(Number((sale.total - (sale.amountPaid || 0)).toFixed(2)));
    const [method, setMethod] = useState<PaymentMethod>('CASH');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [justCreatedNote, setJustCreatedNote] = useState<any>(null);

    const pendingBalance = Number((sale.total - (sale.amountPaid || 0)).toFixed(2));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (amount <= 0) {
            toast.error('El monto debe ser mayor a 0');
            return;
        }

        if (amount > pendingBalance + 1.00) {
            toast.error(`El monto excede el saldo pendiente (${formatCurrency(pendingBalance)})`);
            return;
        }

        try {
            setLoading(true);
            const response = await saleService.createPaymentNote(sale.id, {
                amount,
                method,
                notes
            });
            
            setJustCreatedNote(response.data);
            toast.success(response.message || 'Pago registrado con éxito');
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al registrar el pago');
        } finally {
            setLoading(false);
        }
    };

    if (justCreatedNote) {
        return (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[110] p-4">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-up">
                    <div className="bg-green-600 p-8 text-center text-white">
                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-short">
                            <CheckCircle className="w-12 h-12" />
                        </div>
                        <h3 className="text-2xl font-black mb-1">¡Pago Registrado!</h3>
                        <p className="text-green-100 text-sm opacity-80">Se ha generado una nueva nota de abono</p>
                    </div>
                    
                    <div className="p-8">
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 mb-6">
                            <div className="flex justify-between items-center mb-4">
                                <span className="text-gray-400 text-xs font-black uppercase">Folio Generado</span>
                                <span className="text-purple-700 font-black text-xl">{justCreatedNote.folio}</span>
                            </div>
                            <div className="flex justify-between items-center mb-4">
                                <span className="text-gray-400 text-xs font-black uppercase">Monto Pagado</span>
                                <span className="text-gray-900 font-black text-xl">{formatCurrency(justCreatedNote.total)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-400 text-xs font-black uppercase">Vínculo</span>
                                <span className="text-gray-600 text-sm font-bold">Nota {sale.folio || sale.id}</span>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => {
                                    // Aquí iría la lógica de impresión
                                    toast.success('Abriendo ticket para impresión...');
                                    onSuccess();
                                }}
                                className="w-full bg-purple-600 text-white py-4 rounded-2xl font-black text-lg hover:bg-purple-700 transition-all shadow-xl shadow-purple-100 flex items-center justify-center gap-3"
                            >
                                <Printer className="w-6 h-6" />
                                Imprimir Ticket
                            </button>
                            <button
                                onClick={onSuccess}
                                className="w-full bg-gray-100 text-gray-600 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[110] p-4" onClick={onClose}>
            <div 
                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-up" 
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-xl font-black text-gray-900">Registrar Pago a Nota {sale.folio || sale.id}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8">
                    <div className="mb-8">
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Monto a Pagar</label>
                        <div className="relative">
                            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-3xl font-black text-gray-300">$</span>
                            <input
                                type="number"
                                step="0.01"
                                className="w-full pl-12 pr-6 py-6 bg-gray-50 border-2 border-transparent focus:border-purple-200 focus:bg-white rounded-3xl text-5xl font-black text-purple-700 outline-none transition-all tabular-nums"
                                value={amount}
                                onChange={(e) => setAmount(parseFloat(e.target.value))}
                                max={pendingBalance}
                                required
                                autoFocus
                            />
                        </div>
                        <p className="mt-3 text-sm text-gray-500 flex justify-between px-2">
                            <span>Saldo Pendiente Actual:</span>
                            <span className="font-black text-red-600">{formatCurrency(pendingBalance)}</span>
                        </p>
                    </div>

                    <div className="mb-8">
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Método de Pago</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {[
                                { id: 'CASH', label: 'Efectivo', icon: <Wallet className="w-5 h-5" /> },
                                { id: 'CARD', label: 'Tarjeta', icon: <CreditCard className="w-5 h-5" /> },
                                { id: 'TRANSFER', label: 'Transferencia', icon: <Landmark className="w-5 h-5" /> },
                                { id: 'MERCADO_PAGO', label: 'Mercado Pago', icon: <Layers className="w-5 h-5" /> },
                                { id: 'KUESKI', label: 'Kueski', icon: <DollarSign className="w-5 h-5" /> },
                            ].map((m) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setMethod(m.id as PaymentMethod)}
                                    className={`flex flex-col items-center gap-3 p-4 rounded-2xl border-2 transition-all ${
                                        method === m.id 
                                            ? 'border-purple-600 bg-purple-50 text-purple-700 shadow-md shadow-purple-50' 
                                            : 'border-gray-100 hover:border-gray-200 text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                    {m.icon}
                                    <span className="text-xs font-black uppercase tracking-tight leading-none">{m.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mb-8">
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Notas / Referencia</label>
                        <textarea
                            className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent focus:border-purple-200 focus:bg-white rounded-2xl text-sm outline-none transition-all"
                            placeholder="Ej: Pago con anticipo, referencia bancaria..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-purple-600 text-white py-5 rounded-2xl font-black text-xl hover:bg-purple-700 transition-all shadow-xl shadow-purple-100 disabled:opacity-50 flex items-center justify-center gap-3"
                    >
                        {loading ? 'Procesando...' : (
                            <>
                                <CheckCircle className="w-6 h-6" />
                                Generar Nota de {Math.abs(pendingBalance - amount) < 0.01 ? 'Liquidación' : 'Abono'}
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};
